(() => {
  const STATE_VERSION = 1;
  const STATE_KEY = 'faf:search-return:v1';
  const STATE_LIFETIME = 30 * 60 * 1000;
  const RESTORE_TIMEOUT = 8000;
  let latestInstantTerm = '';
  let restoreStarted = false;

  const safeStorage = {
    get() {
      try {
        return window.sessionStorage.getItem(STATE_KEY);
      } catch (error) {
        return null;
      }
    },
    set(value) {
      try {
        window.sessionStorage.setItem(STATE_KEY, value);
        return true;
      } catch (error) {
        return false;
      }
    },
    remove() {
      try {
        window.sessionStorage.removeItem(STATE_KEY);
      } catch (error) {
        // Storage is optional.
      }
    }
  };

  const relativeUrl = () => (
    window.location.pathname + window.location.search + window.location.hash
  );

  const productHandleFromLink = (link) => {
    if (!link) return '';

    try {
      const pathname = new URL(link.href, window.location.origin).pathname;
      return String(pathname.split('/products/')[1] || '').split('/')[0];
    } catch (error) {
      return '';
    }
  };

  const cardIdentity = (card, link = null) => ({
    id: String(card?.dataset?.productId || ''),
    handle: productHandleFromLink(link || card?.querySelector('a[href*="/products/"]'))
  });

  const sameIdentity = (card, identity = {}) => {
    if (!card) return false;

    const current = cardIdentity(card);
    if (identity.id && current.id === String(identity.id)) return true;
    return Boolean(identity.handle && current.handle === String(identity.handle));
  };

  const validSameWindowClick = (event, link) => {
    if (!link) return false;
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target.toLowerCase() === '_blank') return false;
    return true;
  };

  const readState = () => {
    try {
      const raw = safeStorage.get();
      if (!raw) return null;

      const state = JSON.parse(raw);
      const valid = (
        state &&
        state.version === STATE_VERSION &&
        ['page', 'instant'].includes(state.surface) &&
        state.sourceUrl === relativeUrl() &&
        Number.isFinite(Number(state.savedAt)) &&
        Date.now() - Number(state.savedAt) <= STATE_LIFETIME
      );

      if (!valid) {
        safeStorage.remove();
        return null;
      }

      return state;
    } catch (error) {
      safeStorage.remove();
      return null;
    }
  };

  const shouldRestore = (state) => {
    if (!state) return false;

    const navigationType = window.performance
      ?.getEntriesByType?.('navigation')?.[0]?.type || '';
    const referrerIsProduct = /\/products\//.test(document.referrer || '');

    return navigationType === 'back_forward' || referrerIsProduct;
  };

  const waitForLayout = async () => {
    if (document.fonts?.ready) {
      await Promise.race([
        document.fonts.ready,
        new Promise((resolve) => window.setTimeout(resolve, 350))
      ]).catch(() => {});
    }

    await new Promise((resolve) => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
    });
  };

  const searchPageCards = () =>
  Array.from(
    document.querySelectorAll(SEARCH_PAGE_CARD_SELECTOR)
  );

  const instantCards = () => Array.from(document.querySelectorAll(
    '#instant-product-results .faf-product-card-render[data-product-id]'
  ));

  const findTarget = (cards, state) => {
    if (!cards.length) return null;

    const exact = cards.find((card) => sameIdentity(card, state.product));
    if (exact) return exact;

    const index = Math.min(
      cards.length - 1,
      Math.max(0, Number(state.index || 0))
    );

    return cards[index] || cards[0] || null;
  };

  const SEARCH_PAGE_ROOT_SELECTOR =
  '[data-faf-search-page], [data-faf-search-page-v2]';

  const SEARCH_PAGE_CARD_SELECTOR =
  '[data-faf-search-page] .faf-product-card-render[data-product-id], ' +
  '[data-faf-search-page-v2] .faf-product-card-render[data-product-id]';
  const SEARCH_PAGE_LOAD_MORE_SELECTOR =
  '[data-faf-search-page] [data-search-load-more], ' +
  '[data-faf-search-page-v2] [data-search-load-more]';

  const savePageState = (card, link) => {
    const cards = searchPageCards();
    const index = Math.max(0, cards.indexOf(card));

    safeStorage.set(JSON.stringify({
      version: STATE_VERSION,
      surface: 'page',
      savedAt: Date.now(),
      sourceUrl: relativeUrl(),
      product: cardIdentity(card, link),
      index,
      scrollY: Math.max(0, window.scrollY || window.pageYOffset || 0),
      viewportOffset: card.getBoundingClientRect().top
    }));
  };

  const saveInstantState = (card, link) => {
    const panel = document.getElementById('instant-search-panel');
    const scroller = panel?.querySelector('.faf-search-scroll');
    const cards = instantCards();
    const index = Math.max(0, cards.indexOf(card));
    const scrollerRect = scroller?.getBoundingClientRect();

    safeStorage.set(JSON.stringify({
      version: STATE_VERSION,
      surface: 'instant',
      savedAt: Date.now(),
      sourceUrl: relativeUrl(),
      term: latestInstantTerm,
      product: cardIdentity(card, link),
      index,
      scrollTop: Math.max(0, Number(scroller?.scrollTop || 0)),
      viewportOffset: scrollerRect
        ? card.getBoundingClientRect().top - scrollerRect.top
        : 0
    }));
  };

  const handleProductClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a[href*="/products/"]');
    if (!validSameWindowClick(event, link)) return;

    const pageRoot = link.closest(SEARCH_PAGE_ROOT_SELECTOR);
    if (pageRoot) {
      const card = link.closest('.faf-product-card-render[data-product-id]');
      if (card) savePageState(card, link);
      return;
    }

    const instantRoot = link.closest('#instant-product-results');
    if (!instantRoot) return;

    const card = link.closest('.faf-product-card-render[data-product-id]');
    if (card) saveInstantState(card, link);
  };

  const waitForSearchAppend = (
  button,
  timeout = 4000
) =>
  new Promise((resolve) => {
    let settled = false;

    const finish = (appended) => {
      if (settled) return;

      settled = true;

      window.clearTimeout(timeoutId);

      window.removeEventListener(
        'faf:search:append',
        onAppend
      );

      resolve(appended);
    };

    const onAppend = () => finish(true);

    const timeoutId = window.setTimeout(
      () => finish(false),
      timeout
    );

    window.addEventListener(
      'faf:search:append',
      onAppend,
      { once: true }
    );

    button.click();
  });

const ensureSearchPageDepth = async (state) => {
  const targetIndex = Math.max(
    0,
    Number(state.index || 0)
  );

  const startedAt = Date.now();

  while (
    searchPageCards().length <= targetIndex
  ) {
    const elapsed =
      Date.now() - startedAt;

    const remaining =
      RESTORE_TIMEOUT - elapsed;

    if (remaining <= 0) break;

    const button =
      document.querySelector(
        SEARCH_PAGE_LOAD_MORE_SELECTOR
      );

    if (!button) break;

    const appended =
      await waitForSearchAppend(
        button,
        Math.min(
          4000,
          remaining
        )
      );

    if (!appended) break;

    await waitForLayout();
  }
};

    const restorePage = async (state) => {
    await waitForLayout();

    await ensureSearchPageDepth(state);

    await waitForLayout();

    const target = findTarget(
      searchPageCards(),
      state
    );
    if (!target) {
      window.scrollTo(0, Math.max(0, Number(state.scrollY || 0)));
      return false;
    }

    const desiredOffset = Number.isFinite(Number(state.viewportOffset))
      ? Number(state.viewportOffset)
      : 100;

    const position = () => {
      const top = window.scrollY + target.getBoundingClientRect().top - desiredOffset;
      window.scrollTo(0, Math.max(0, top));
    };

    position();

    window.setTimeout(() => {
      if (document.visibilityState === 'visible') position();
    }, 220);

    safeStorage.remove();
    return true;
  };

  const waitForInstantTarget = (state) => new Promise((resolve) => {
    const startedAt = Date.now();

    const check = () => {
      const panel = document.getElementById('instant-search-panel');
      const scroller = panel?.querySelector('.faf-search-scroll');
      const cards = instantCards();
      const target = findTarget(cards, state);

      if (panel && scroller && target && cards.length) {
        resolve({ panel, scroller, target });
        return;
      }

      if (Date.now() - startedAt >= RESTORE_TIMEOUT) {
        resolve(null);
        return;
      }

      window.setTimeout(check, 80);
    };

    check();
  });

  const restoreInstant = async (state) => {
    if (!state.term) {
      safeStorage.remove();
      return false;
    }

    latestInstantTerm = state.term;
    window.dispatchEvent(new CustomEvent('search', { detail: { q: state.term } }));

    const result = await waitForInstantTarget(state);
    if (!result) return false;

    await waitForLayout();

    const desiredOffset = Number.isFinite(Number(state.viewportOffset))
      ? Number(state.viewportOffset)
      : 0;

    const position = () => {
      const scrollerRect = result.scroller.getBoundingClientRect();
      const currentOffset = result.target.getBoundingClientRect().top - scrollerRect.top;
      const nextScrollTop = result.scroller.scrollTop + currentOffset - desiredOffset;
      result.scroller.scrollTo({ top: Math.max(0, nextScrollTop), behavior: 'auto' });
    };

    position();

    window.setTimeout(() => {
      if (document.visibilityState === 'visible') position();
    }, 220);

    safeStorage.remove();
    return true;
  };

  const restore = async () => {
    if (restoreStarted) return;

    const state = readState();
    if (!shouldRestore(state)) return;

    restoreStarted = true;

    if (state.surface === 'instant') {
      await restoreInstant(state);
      return;
    }

    await restorePage(state);
  };

  window.addEventListener('search', (event) => {
    if (!(event instanceof CustomEvent)) return;
    const term = String(event.detail?.q || '').trim();
    if (term) latestInstantTerm = term;
  });

  document.addEventListener('click', handleProductClick, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restore, { once: true });
  } else {
    restore();
  }

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    restoreStarted = false;
    restore();
  });
})();
